import { z } from "zod";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/http";
import { zSpringPage, zTimestamp } from "./_schemas";

export const workflowSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  status: z.string().nullish(),
  steps: z.string().nullish(), // JSON string
  archivedAt: zTimestamp.nullish(),
  createdAt: zTimestamp.nullish(),
  updatedAt: zTimestamp.nullish(),
});
export type Workflow = z.infer<typeof workflowSchema>;

export const workflowInstanceSchema = z.object({
  id: z.string(),
  workflowId: z.string().nullish(),
  status: z.string(),
  currentStepIndex: z.number().nullish(),
  triggeredBy: z.string().nullish(),
  startedAt: zTimestamp.nullish(),
  completedAt: zTimestamp.nullish(),
  errorMessage: z.string().nullish(),
});
export type WorkflowInstance = z.infer<typeof workflowInstanceSchema>;

export interface WorkflowStep {
  type: string;
  name?: string;
  [k: string]: unknown;
}

export function parseSteps(json: string | null | undefined): WorkflowStep[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as WorkflowStep[]) : (parsed.steps ?? []);
  } catch {
    return [];
  }
}

export function useWorkflows(includeArchived = false) {
  return useQuery({
    queryKey: ["workflows", "list", includeArchived],
    queryFn: () =>
      apiRequest<unknown>(`/workflows?size=200&includeArchived=${includeArchived}`).then((r) =>
        zSpringPage(workflowSchema).parse(r),
      ),
  });
}

export function workflowQuery(id: string) {
  return queryOptions({
    queryKey: ["workflows", "detail", id],
    queryFn: () => apiRequest<unknown>(`/workflows/${id}`).then((r) => workflowSchema.parse(r)),
  });
}
export function useWorkflow(id: string) {
  return useQuery(workflowQuery(id));
}

export function useWorkflowInstances(id: string) {
  return useQuery({
    queryKey: ["workflows", "instances", id],
    queryFn: () =>
      apiRequest<unknown>(`/workflows/${id}/instances?size=50`).then((r) =>
        zSpringPage(workflowInstanceSchema).parse(r),
      ),
  });
}

export function useWorkflowMutations(id?: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["workflows"] });
  return {
    remove: useMutation({
      mutationFn: (wid: string) => apiRequest<void>(`/workflows/${wid}`, { method: "DELETE" }),
      onSuccess: invalidate,
    }),
    create: useMutation({
      mutationFn: (body: { name: string; description?: string | null; steps?: WorkflowStep[] }) =>
        apiRequest<unknown>("/workflows", { method: "POST", body }).then((r) =>
          workflowSchema.parse(r),
        ),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: (payload: {
        wid: string;
        body: { name?: string; description?: string | null; steps?: WorkflowStep[] };
      }) =>
        apiRequest<unknown>(`/workflows/${payload.wid}`, {
          method: "PUT",
          body: payload.body,
        }).then((r) => workflowSchema.parse(r)),
      onSuccess: invalidate,
    }),
    trigger: useMutation({
      mutationFn: (wid: string) =>
        apiRequest<unknown>(`/workflows/${wid}/trigger`, { method: "POST", body: {} }),
      onSuccess: invalidate,
    }),
    duplicate: useMutation({
      mutationFn: (wid: string) =>
        apiRequest<unknown>(`/workflows/${wid}/duplicate`, { method: "POST" }).then((r) =>
          workflowSchema.parse(r),
        ),
      onSuccess: invalidate,
    }),
    setArchived: useMutation({
      mutationFn: ({ wid, archived }: { wid: string; archived: boolean }) =>
        apiRequest<void>(`/workflows/${wid}/${archived ? "archive" : "unarchive"}`, {
          method: "PATCH",
        }),
      onSuccess: invalidate,
    }),
    approve: useMutation({
      mutationFn: (instanceId: string) =>
        apiRequest<unknown>(`/workflows/instances/${instanceId}/approve`, { method: "POST" }),
      onSuccess: () => {
        invalidate();
        if (id) qc.invalidateQueries({ queryKey: ["workflows", "instances", id] });
      },
    }),
    cancel: useMutation({
      mutationFn: (instanceId: string) =>
        apiRequest<unknown>(`/workflows/instances/${instanceId}/cancel`, { method: "POST" }),
      onSuccess: () => {
        invalidate();
        if (id) qc.invalidateQueries({ queryKey: ["workflows", "instances", id] });
      },
    }),
  };
}
